<script setup lang="ts">
import type { Course } from '~/data/courses'
import type { Enrolment } from '~/data/enrolments'
import type { Program } from '~/data/programs'

const { getProgramPartnerTitle } = useStaticData()

const props = defineProps({
  program: {
    type: Object as PropType<Program>,
    required: true
  },
  course: { type: Object as PropType<Course>, required: true },
  enrolment: { type: Object as PropType<Enrolment>, required: true }
})

function goToProgramme() {
  if (props.program.externalLink) {
    window.open(props.program.externalLink.link, '_blank', props.program.externalLink.linkTarget)
  } else navigateTo(`/programmes/${props.program.id}`)
}
</script>
<template>
  <div class="course-pgm">
    <img
      :src="`/images/database/programmes/card-banners/${props.program.id}.jpg`"
      :alt="program.title"
      class="course-pgm-img"
    >

    <div class="course-pgm-text">
      <div>
        <h2>{{ program.title }}</h2>

        <div class="course-pgm-partner">
          <img
            :src="`/images/database/programmes/logos/${program.partner}.png`"
            :alt="getProgramPartnerTitle(program.partner)"
          >

          <span>{{ getProgramPartnerTitle(program.partner) }}</span>
        </div>

        <p v-html="program.subtext" />
      </div>

      <CommonArrowLink title="Learn more" @click="goToProgramme" />
    </div>

    <div>
      <CommonButton arrow @click="goToProgramme">Apply now</CommonButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$imgSize: 90px;

.course-pgm {
  border: 1px solid gainsboro;
  padding: 35px;
  border-radius: 15px;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: $imgSize 1fr auto;
  gap: 35px;

  &-img {
    height: $imgSize;
    width: $imgSize;
    object-fit: cover;
    border-radius: 10px;
  }

  &-text {
    padding-right: 15%;

    h2 {
      font-size: 1.5rem;
      line-height: 2rem;
      margin-bottom: 15px;
    }

    p {
      margin: 20px 0 7px;
      color: var(--textLight);
      font-size: 0.9rem;
      line-height: 1.4rem;
    }
  }

  &-partner {
    display: flex;
    align-items: center;

    img {
      $iconSize: 35px;
      height: $iconSize;
      width: $iconSize;
      border-radius: 50%;
      border: 1px solid gainsboro;
      margin-right: 15px;
    }

    span {
      font-weight: 500;
      font-size: 0.95rem;
      color: var(--textMedium);
    }
  }

  :deep(.btn-arrow) {
    font-size: 0.95rem;
    padding: 10px 20px 10px 25px;
  }
}

@media (max-width: 1100px) {
  $imgSize: 80px;

  .course-pgm {
    padding: 25px;
    gap: 25px;
    grid-template-columns: $imgSize 1fr auto;

    &-img {
      height: $imgSize;
      width: $imgSize;
    }

    &-text {
      padding-right: 20px;

      h2 {
        font-size: 1.3rem;
        line-height: 1.8rem;
        margin-bottom: 12px;
      }

      p {
        margin: 15px 0 10px;
      }
    }
  }
}

@media (max-width: 1000px) {
  .course-pgm {
    display: flex;
    flex-wrap: wrap;

    &-text {
      width: calc(100% - 120px);

      p {
        margin-bottom: 5px;
      }
    }

    :deep(.btn-arrow) {
      margin-left: 100px;
    }
  }
}

@media (max-width: 900px) {
  .course-pgm {
    display: grid;
    grid-template-columns: 80px 1fr auto;
    padding: 30px;
    gap: 30px;

    &-text {
      width: auto;
    }

    :deep(.btn-arrow) {
      margin-left: 0;
    }
  }
}

@media (max-width: 700px) {
  .course-pgm {
    display: flex;
    flex-wrap: wrap;
    padding: 25px;
    gap: 25px;

    &-text {
      width: calc(100% - 120px);
    }

    :deep(.btn-arrow) {
      margin-left: 100px;
    }
  }
}

@media (max-width: 600px) {
  .course-pgm {
    padding: 20px;
    gap: 20px;
    justify-content: center;

    $imgSize: 60px;

    &-img {
      height: $imgSize;
      width: $imgSize;
    }

    &-text {
      width: 100%;
      padding-right: 10px;
      margin-left: -80px;

      h2 {
        font-size: 1.1rem;
        line-height: 1.6rem;
        min-height: $imgSize;
        display: flex;
        align-items: center;
        padding-left: 80px;
        margin-bottom: 17px;
      }

      p {
        margin: 17px 0 5px;
      }

      :deep(.arrow-link) {
        span {
          font-size: 0.9rem;
        }

        svg {
          display: none;
        }
      }
    }

    &-partner {
      img {
        $iconSize: 30px;
        height: $iconSize;
        width: $iconSize;
        margin-right: 10px;
      }

      span {
        font-size: 0.9rem;
      }
    }

    :deep(.btn-arrow) {
      margin-left: auto;
      margin-right: auto;
    }
  }
}
</style>
